import React, {useState, useEffect} from "react";
import {useNavigate} from "react-router-dom";
import {limitErrorEmitter} from "../../AxiosInstance";
import Modal from "../modal/modal";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faExclamationTriangle} from "@fortawesome/free-solid-svg-icons";

const codeToType = {
    'MAX_COMPANIES_REACHED': 'companies',
    'MAX_DEVICES_REACHED': 'devices',
    'MAX_USERS_REACHED': 'users',
};

const typeLabels = {
    companies: 'companies',
    devices: 'devices',
    users: 'users',
};

function getTitle(code, subscriptionStatus) {
    if (code === 'NO_SUBSCRIPTION') return 'No Active Subscription';
    if (code === 'SUBSCRIPTION_NOT_ACTIVE') {
        if (subscriptionStatus === 'pending') return 'Subscription Pending';
        if (subscriptionStatus === 'suspended') return 'Subscription Suspended';
        if (subscriptionStatus === 'cancelled') return 'Subscription Cancelled';
        return 'Subscription Not Active';
    }
    const type = codeToType[code];
    if (type === 'companies') return 'Company Limit Reached';
    if (type === 'devices') return 'Device Limit Reached';
    if (type === 'users') return 'User Limit Reached';
    return 'Limit Reached';
}

function getMessage(code, subscriptionStatus, current, max, errorMsg) {
    if (code === 'NO_SUBSCRIPTION') {
        return 'You don\'t have an active subscription. Select a plan to start using all features.';
    }
    if (code === 'SUBSCRIPTION_NOT_ACTIVE') {
        if (subscriptionStatus === 'pending') return 'Your subscription request is being reviewed by the administrator. You will be notified once it is approved.';
        if (subscriptionStatus === 'suspended') return 'Your subscription has been suspended. Contact the administrator for more information.';
        if (subscriptionStatus === 'cancelled') return 'Your subscription has been cancelled. Activate a new plan to continue.';
        return 'Your subscription is not active. You need an active plan to use this feature.';
    }
    const type = codeToType[code];
    if (type && max) {
        const label = typeLabels[type];
        return `Your current plan allows a maximum of ${max} ${label}. You currently have ${current}.`;
    }
    return errorMsg || 'You have reached the limit of your current plan.';
}

export default function LimitErrorProvider({children}) {
    const navigate = useNavigate();
    const [show, setShow] = useState(false);
    const [errorData, setErrorData] = useState({});

    useEffect(() => {
        const handler = (e) => {
            setErrorData(e.detail);
            setShow(true);
        };
        limitErrorEmitter.addEventListener('limit-error', handler);
        return () => limitErrorEmitter.removeEventListener('limit-error', handler);
    }, []);

    const {code, subscriptionStatus, current, max, error: errorMsg} = errorData;
    const isSubscriptionPending = code === 'SUBSCRIPTION_NOT_ACTIVE' && subscriptionStatus === 'pending';
    const type = codeToType[code] || '';

    const handleUpgrade = () => {
        setShow(false);
        if (isSubscriptionPending) {
            navigate('/settings/payments');
        } else {
            navigate('/pricing');
        }
    };

    return (
        <>
            {children}
            <Modal
                show={show}
                setter={setShow}
                name={getTitle(code, subscriptionStatus)}
                buttonName={isSubscriptionPending ? 'View Status' : 'View Plans'}
                onClick={handleUpgrade}
            >
                <div style={{textAlign: 'center', padding: '20px 0'}}>
                    <div style={{
                        width: '64px',
                        height: '64px',
                        borderRadius: '50%',
                        background: '#FEF3C7',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 20px',
                        fontSize: '28px',
                        color: '#F59E0B'
                    }}>
                        <FontAwesomeIcon icon={faExclamationTriangle}/>
                    </div>

                    <p style={{fontSize: '16px', color: '#1E293B', marginBottom: '12px'}}>
                        {getMessage(code, subscriptionStatus, current, max, errorMsg)}
                    </p>

                    {type && (
                        <p style={{fontSize: '14px', color: '#64748B', lineHeight: '1.6'}}>
                            To add more {typeLabels[type] || 'resources'},
                            you need to upgrade your plan to one with greater capacity.
                        </p>
                    )}

                    <div style={{
                        backgroundColor: '#F0FDF4',
                        border: '1px solid #BBF7D0',
                        borderRadius: '8px',
                        padding: '12px',
                        marginTop: '16px'
                    }}>
                        <p style={{fontSize: '13px', color: '#166534', margin: 0}}>
                            Upgrade your plan to unlock more features and capacity.
                        </p>
                    </div>
                </div>
            </Modal>
        </>
    );
}
